import React, { useState } from 'react';

import ProfileBanner from './ProfileBanner';
import { follow, unfollow } from '../services/user';
import i18n from '../utils/i18n';
import SubscriptionStatus from '../enums/SubscriptionStatus';
import { formatCurrency } from '../utils/data';
import { format } from 'date-fns';
import { trackFollowEvent } from '../utils/analytics';

import { Tooltip, OverlayTrigger } from 'react-bootstrap';

type Props = {
  metadata: Metadata;
  username: string;
  plan: Plan;
  onSubscribe: () => void;
};

const ProfileCard: React.FC<Props> = ({
  metadata: { subscribed, followed, profile, creatorId, subscriptionCancelAt },
  username,
  plan,
  onSubscribe,
}) => {
  const [isFollowing, setIsFollowing] = useState(followed);

  const onFollow = async () => {
    try {
      await follow(creatorId);
      // Rastrear evento de seguir creator
      trackFollowEvent('follow', creatorId);
      setIsFollowing(!isFollowing);
    } catch (error) {
      console.log(error);
    }
  };

  const onUnfollow = async () => {
    try {
      await unfollow(creatorId);
      // Rastrear evento de deixar de seguir creator
      trackFollowEvent('unfollow', creatorId);
      setIsFollowing(!isFollowing);
    } catch (error) {
      console.log(error);
    }
  };

  const renderSubscriptionButton = () => {
    if (!plan.value) return null;

    if (subscribed === SubscriptionStatus.ACTIVE && subscriptionCancelAt) {
      const formattedCancelDate = subscriptionCancelAt
        ? format(new Date(subscriptionCancelAt), 'dd/MM/yyyy')
        : null;

      return (
        <div className="d-flex flex-row justify-content-center align-items-center">
          <div 
            className="fw-bold px-3 py-2 rounded-pill d-flex align-items-center" 
            style={{ 
              background: 'linear-gradient(135deg, #FF4B6E 0%, #FF9A56 100%)',
              color: '#FFFFFF',
              boxShadow: '0 4px 15px rgba(255, 75, 110, 0.3)',
              border: 'none',
              transition: 'all 0.2s ease-in-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(255, 75, 110, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(255, 75, 110, 0.3)';
            }}
          >
            <i className="feather-star me-2"></i>
            <span className="font-xsss">Assinante</span>
          </div>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="tooltip-top">
                Assinatura válida até {formattedCancelDate}.
              </Tooltip>
            }
          >
            <i className="font-sm fw-600 mb-1 feather-info text-warning ms-2"></i>
          </OverlayTrigger>
        </div>
      );
    }

    if (subscribed === SubscriptionStatus.ACTIVE) {
      return (
        <div className="d-flex flex-row justify-content-center align-items-center">
          <div 
            className="fw-bold px-3 py-2 rounded-pill d-flex align-items-center" 
            style={{ 
              background: 'linear-gradient(135deg, #FF4B6E 0%, #FF9A56 100%)',
              color: '#FFFFFF',
              boxShadow: '0 4px 15px rgba(255, 75, 110, 0.3)',
              border: 'none',
              transition: 'all 0.2s ease-in-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(255, 75, 110, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(255, 75, 110, 0.3)';
            }}
          >
            <i className="feather-star me-2"></i>
            <span className="font-xsss">Assinante</span>
            <i className="feather-check ms-2"></i>
          </div>
        </div>
      );
    }

    if (subscribed === SubscriptionStatus.PROCESSING) {
      return (
        <div className="d-flex flex-row justify-content-center align-items-center">
          <h4 className="fw-600 text-warning font-xsss mb-0 mt-0 me-2">
            Processando o pagamento
          </h4>
          <i className="font-sm fw-600 mb-1 feather-loader text-warning"></i>
        </div>
      );
    }

    return (
      <button
        onClick={onSubscribe}
        className="btn p-2 m-2 rounded-md font-xssss fw-700 profile-card-buttons-subscriptions-buttons"
        style={{ 
          background: 'linear-gradient(135deg, #FF4B6E 0%, #FF9A56 100%)',
          color: '#FFFFFF',
          boxShadow: '0 4px 15px rgba(255, 75, 110, 0.3)',
          border: 'none',
          transition: 'all 0.2s ease-in-out',
          position: 'relative',
          overflow: 'hidden'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 8px 25px rgba(255, 75, 110, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 15px rgba(255, 75, 110, 0.3)';
        }}
      >
        {i18n.t<string>('ProfileCard.subscribe', {
          value: formatCurrency(plan.value / 100),
        })}
      </button>
    );
  };

  const renderFollowButton = () => {
    if (subscribed === SubscriptionStatus.ACTIVE) return null;

    if (!isFollowing) {
      return (
        <button
          onClick={onFollow}
          className="btn p-2 m-2 rounded-md font-xssss fw-700 profile-card-buttons-subscriptions-buttons"
          style={{ 
            background: 'linear-gradient(135deg, #FF4B6E 0%, #FF9A56 100%)',
            color: '#FFFFFF',
            boxShadow: '0 4px 15px rgba(255, 75, 110, 0.3)',
            border: 'none',
            transition: 'all 0.2s ease-in-out',
            position: 'relative',
            overflow: 'hidden'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(255, 75, 110, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(255, 75, 110, 0.3)';
          }}
        >
          {i18n.t<string>('ProfileCard.follow')}
          <i
            className={`feather-user-plus font-xsss text-white fw-800`}
            style={{ cursor: 'pointer', marginLeft: 5 }}
          />
        </button>
      );
    }
    return (
      <button
        onClick={onUnfollow}
        className="btn p-2 m-2 rounded-md font-xssss fw-700 profile-card-buttons-subscriptions-buttons"
        style={{ 
          background: 'linear-gradient(135deg, #6B7280 0%, #9CA3AF 100%)',
          color: '#FFFFFF',
          boxShadow: '0 4px 15px rgba(107, 114, 128, 0.3)',
          border: 'none',
          transition: 'all 0.2s ease-in-out',
          position: 'relative',
          overflow: 'hidden'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 8px 25px rgba(107, 114, 128, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 15px rgba(107, 114, 128, 0.3)';
        }}
      >
        {i18n.t<string>('ProfileCard.following')}
        <i
          className={`feather-user-check font-xsss text-white fw-800`}
          style={{ cursor: 'pointer', marginLeft: 5 }}
        />
      </button>
    );
  };

  return (
    <div className="card w-100 border-0 p-0 bg-white shadow-xss rounded-xxl">
      <ProfileBanner profile={profile} />
      <div className="card-body p-0 position-relative">
        <figure
          className="avatar position-absolute w100 z-index-1"
          style={{ top: '-40px', left: '30px' }}
        >
          <img
            src={`${profile.avatar ?? 'assets/images/user-100x100.png'}`}
            alt="avater"
            className="float-right p-1 bg-white rounded-circle w-100 h-100"
            style={{
              width: 100,
              height: 100,
              objectFit: 'cover'
            }}
          />
        </figure>
        <div className="d-flex flex-column">
          <h4 className="fw-700 font-sm mt-2 pl-15">{username}</h4>
          <h4 className="fw-500 font-xssse mb-5 pl-15">{`@${username}`}</h4>
        </div>
      </div>
      <div className="card-body d-block w-100 shadow-none mb-0 p-0 border-top-xs">
        <div className="d-flex justify-content-between align-items-center profile-card-buttons">
          <div className="px-2 py-2">
            {/* <img
              src="assets/images/instagram-logo.png"
              style={{
                maxHeight: 35,
                maxWidth: 35,
                cursor: 'pointer',
                marginRight: 10,
              }}
              alt=""
              onClick={
                () => false
                // window.open('https://www.instagram.com/', '_blank')
              }
            /> */}
            {/* <img
              src="assets/images/x-logo.png"
              style={{
                maxHeight: 25,
                maxWidth: 25,
                cursor: 'pointer',
                marginRight: 10,
              }}
              alt=""
              onClick={() => false}
            /> */}
            {/* <img
              src="assets/images/tiktok.png"
              style={{
                maxHeight: 25,
                maxWidth: 25,
                cursor: 'pointer',
                marginRight: 8,
              }}
              alt=""
              onClick={() => false}
            /> */}
          </div>
          <div className="d-flex profile-card-buttons-subscriptions p-2">
            {renderSubscriptionButton()}
            {renderFollowButton()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileCard;
